import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Product {
  id: string;
  wordpress_id: number;
  name: string;
  slug: string;
  description: string | null;
  short_description: string | null;
  price: number;
  regular_price: number | null;
  sale_price: number | null;
  sku: string | null;
  stock_quantity: number;
  stock_status: string;
  featured: boolean;
  permalink: string | null;
  product_images: Array<{
    id: string;
    src: string;
    alt: string | null;
    position: number;
  }>;
  product_categories?: Array<{
    category: {
      id: string;
      wordpress_id: number;
      name: string;
      slug: string;
    };
  }>;
}

export const useProducts = (featured?: boolean) => {
  return useQuery<Product[]>({
    queryKey: ["products", featured],
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    queryFn: async () => {
      console.log("🔍 Fetching products from Supabase...");
      
      let query = supabase
        .from("products")
        .select(`
          *,
          product_images(id, src, alt, position, product_id),
          product_categories(
            category:categories(id, wordpress_id, name, slug)
          )
        `)
        .order("created_at", { ascending: false });

      if (featured) {
        query = query.eq("featured", true);
      }

      const { data, error } = await query;

      if (error) {
        console.error("❌ Error fetching products:", error);
        throw error;
      }
      
      console.log("✅ Products fetched:", data?.length, "products");
      console.log("📸 First product images:", data?.[0]?.product_images);
      
      // Sortează imaginile după position și filtrează produsele fără imagini
      const productsWithSortedImages = data
        ?.map(product => ({
          ...product,
          product_images: product.product_images?.sort((a: any, b: any) => a.position - b.position) || []
        }))
        .filter(product => product.product_images && product.product_images.length > 0);
      
      console.log("📦 Returning products with images:", productsWithSortedImages?.length);
      return productsWithSortedImages as Product[];
    },
  });
};

export const useProduct = (slug: string) => {
  return useQuery({
    queryKey: ["product", slug],
    enabled: !!slug,
    staleTime: 0,
    refetchOnMount: true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select(`
          *,
          product_images(*),
          product_categories(
            category:categories(*)
          )
        `)
        .eq("slug", slug)
        .single();

      if (error) throw error;
      
      // Sortează imaginile după position
      if (data.product_images) {
        data.product_images.sort((a: any, b: any) => a.position - b.position);
      }
      
      return data as Product;
    },
  });
};
